// PharmaCrowd – Wallet Activities Preview · Stable V3
// New UI with old logic integration

import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { fetchAllCurrencies1 } from "@/redux/actions/FetchCoinLists";
import { getTopFiveBalance, startKYC, getUserPendingBalanceWebapp, userInternalWalletBalanceRefresh, walletSendReceiveActivities } from "../../../utils/services/user.service";

// Components
import StatusBanner from "./components/StatusBanner";
import WalletBlock from "./components/WalletBlock";
import BankWalletBlock from "./components/BankWalletBlock";
import SwapCard from "./components/SwapCard";
import IbanCard from "./components/IbanCard";
import SendWithdrawCard from "./components/SendWithdrawCard";
import WalletOverview from "./components/WalletOverview";
import NotificationModal from "./components/NotificationModal";

// Styled Components
import { GlobalStyle, Page, Card } from "./components/styledComponents";

// Utils & Hooks
import { useDashboardData } from "./hooks/useDashboardData";
import {
  transformWalletData,
  transformBankData,
  transformVirtualWalletData,
  transformOnchainWalletData,
  getCategoryTotals,
} from "./utils/walletDataTransformers";

// Mock IBAN movements data
const IBAN_MOVEMENTS = [
  { ts: "2025-11-01 10:05", direction: "in", currency: "EUR", amount: 2500.0, desc: "Bonifico SEPA da Cliente A", ref: "SEPA-2025-001" },
  { ts: "2025-11-02 15:40", direction: "out", currency: "EUR", amount: 980.5, desc: "Pagamento fornitore", ref: "SEPA-2025-002" },
  { ts: "2025-11-03 09:12", direction: "in", currency: "EUR", amount: 12000.0, desc: "Incasso fattura 104", ref: "SEPA-2025-003" },
  { ts: "2025-11-04 18:20", direction: "out", currency: "EUR", amount: 3000.0, desc: "Cash-out verso banca esterna", ref: "SEPA-2025-004" },
];

export default function NewDashboard() {
  const dispatch = useDispatch();
  const history = useHistory();
  const [section, setSection] = useState("Wallet");
  const [tabVirtual, setTabVirtual] = useState("coin");
  const [tabOnchain, setTabOnchain] = useState("coin");
  const [notification, setNotification] = useState(null);
  const [lastWallet, setLastWallet] = useState("Virtual");
  const [swap, setSwap] = useState({ from: "CTY", to: "USDT", amount: "" });
  const [addressViewWallet, setAddressViewWallet] = useState(null); // "Virtual" | "On-chain" | "Bank" | null
  const [activity, setActivity] = useState(null); // { wallet: "Virtual" | "On-chain" | "Bank", symbol: string } | null
  const [movements, setMovements] = useState([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);

  // Use custom hook for data fetching
  const {
    isLoading,
    kycStatus,
    walletData,
    onchainWalletData,
    bankData,
    pendingBalance,
    setPendingBalance,
    setWalletData,
    setOnchainWalletData,
  } = useDashboardData();

  const { totalBalance } = useSelector(state => state.sidebar);
  const { coinList: virtualWalletCoins } = useSelector(state => ({
    coinList: state.coinList1?.data || [],
  }));

  // Transform virtual wallet data from Redux state
  const transformVirtualWalletDataMemo = useMemo(() => {
    return transformVirtualWalletData(virtualWalletCoins);
  }, [virtualWalletCoins]);

  const virtualItems = useMemo(() => {
    return transformVirtualWalletDataMemo.filter((a) => a.type === tabVirtual);
  }, [transformVirtualWalletDataMemo, tabVirtual]);

  const transformOnchainWalletDataMemo = useMemo(() => {
    const onchainSource = onchainWalletData || walletData?.onchain?.[0]?.topBalances || [];
    return transformOnchainWalletData(onchainSource);
  }, [onchainWalletData, walletData]);

  const onchainItems = useMemo(() => {
    return transformOnchainWalletDataMemo.filter((a) => a.type === tabOnchain);
  }, [transformOnchainWalletDataMemo, tabOnchain]);

  const bankItems = useMemo(() => transformBankData(bankData), [bankData]);

  const virtualTotalsAll = useMemo(() => {
    return getCategoryTotals(transformVirtualWalletDataMemo);
  }, [transformVirtualWalletDataMemo]);

  const onchainTotalsAll = useMemo(() => {
    return getCategoryTotals(transformOnchainWalletDataMemo);
  }, [transformOnchainWalletDataMemo]);

  // Use API-provided totals instead of calculating
  const virtualTotal = useMemo(() => {
    // Get from localStorage (set by fetchAllCurrencies1) or calculate from items
    const storedTotal = localStorage.getItem("internalCoins");
    if (storedTotal) {
      return parseFloat(storedTotal.replace(/"/g, '')) || 0;
    }
    return transformVirtualWalletDataMemo.reduce((acc, a) => acc + a.balance, 0);
  }, [transformVirtualWalletDataMemo]);

  const onchainTotal = useMemo(() => {
    return transformOnchainWalletDataMemo.reduce((acc, a) => acc + a.balance, 0);
  }, [transformOnchainWalletDataMemo]);

  const bankTotal = useMemo(() => {
    // Use API-provided total from getUserTopFiveStrigaBalance
    if (bankData?.totalBalance) {
      return parseFloat(bankData.totalBalance) || 0;
    }
    return bankItems.reduce((acc, a) => acc + a.balance, 0);
  }, [bankData, bankItems]);

  const totalBalanceCalc = (parseFloat(totalBalance) || 0) + virtualTotal + onchainTotal + bankTotal;

  const [messageLevel, messageText] = useMemo(() => {
    if (kycStatus === "approved") {
      return ["info", "KYC completed. You have full access to Wallet and OTC functionalities."];
    }
    if (kycStatus === "pending") {
      return ["warning", "Your KYC is under review. Alcuni limiti potrebbero essere applicati fino all'approvazione."];
    }
    return ["block", "Complete the KYC to unlock withdrawals and higher limits on OTC & Wallet."];
  }, [kycStatus]);

  const openNotification = (title, message) => {
    setNotification({ title, message });
  };

  const handleKYC = async () => {
    try {
      const res = await startKYC();
      if (res?.data?.verification?.url) {
        window.open(res.data.verification.url, "_blank");
      }
    } catch (error) {
      openNotification("KYC", "Error starting KYC process");
    }
  };

  const handleSwap = () => {
    if (!swap.amount) {
      openNotification("Swap", "Inserisci un importo per lo swap.");
      return;
    }
    openNotification("Swap preview", `Swap ${swap.amount} ${swap.from} → ${swap.to}`);
  };

  const handleIbanAction = (msg) => openNotification("IBAN", msg);

  // Get user data for addresses
  const userData = JSON.parse(localStorage.getItem("user"))?.data?.user || {};

  // Prepare address data for Virtual Wallet
  const virtualAddresses = useMemo(() => {
    const addresses = [];
    if (userData.address) {
      addresses.push({
        chain: "BNB/ETH/MATIC",
        label: "BNB/ETH/MATIC Custodial",
        address: userData.address,
        network: "BSC/Ethereum/Polygon",
      });
    }
    if (userData.trc_address) {
      addresses.push({
        chain: "TRON",
        label: "TRON Wallet",
        address: userData.trc_address,
        network: "TRON",
      });
    }
    if (userData.sol_address) {
      addresses.push({
        chain: "SOL",
        label: "Solana Wallet",
        address: userData.sol_address,
        network: "Solana",
      });
    }
    if (userData.btcAddress) {
      addresses.push({
        chain: "BTC",
        label: "BTC Custodial",
        address: userData.btcAddress,
        network: "BTC",
      });
    }
    return addresses;
  }, [userData]);

  // Prepare address data for On-chain Wallet
  const onchainAddresses = useMemo(() => {
    const addresses = [];
    if (userData.secondary_wallet_address) {
      addresses.push({
        chain: "BNB/ETH/MATIC",
        label: "Main On-chain",
        address: userData.secondary_wallet_address,
        network: "BSC/Ethereum/Polygon",
      });
    }
    if (userData.secondary_wallet_sol_address) {
      addresses.push({
        chain: "SOL",
        label: "Solana On-chain",
        address: userData.secondary_wallet_sol_address,
        network: "Solana",
      });
    }
    if (userData.secondary_wallet_trc_address) {
      addresses.push({
        chain: "TRON",
        label: "TRON On-chain",
        address: userData.secondary_wallet_trc_address,
        network: "TRON",
      });
    }
    return addresses;
  }, [userData]);

  // Prepare bank account data (IBAN)
  const bankAddresses = useMemo(() => {
    const addresses = [];
    // This would typically come from an API call to getUserInfo()
    // For now, we'll check if there's IBAN data in user info
    // You may need to fetch this from getUserInfo() API
    if (userData.striga_iban_no) {
      addresses.push({
        currency: "EUR",
        label: "Conto Principale EUR",
        iban: userData.striga_iban_no,
        holder: `${userData.firstName || ""} ${userData.lastName || ""}`.trim() || "User",
        network: "SEPA",
      });
    }
    return addresses;
  }, [userData]);

  const handleWalletDetails = (wallet) => {
    // Clear activity when showing address view
    setActivity(null);
    setAddressViewWallet(wallet === addressViewWallet ? null : wallet);
  };

  const handleBackFromAddress = (wallet) => {
    setAddressViewWallet(null);
  };

  const handleRowClick = (wallet, symbol) => {
    // Clear address view when showing activity
    setAddressViewWallet(null);
    const newActivity = activity && activity.wallet === wallet && activity.symbol === symbol ? null : { wallet, symbol };
    setActivity(newActivity);
    setLastWallet(wallet);
  };

  const handleBackFromActivity = (wallet) => {
    setActivity(null);
    setMovements([]);
  };

  // Map wallet type to trx_status for API
  const getTrxStatus = (wallet) => {
    const mapping = {
      "Virtual": "virtual",
      "On-chain": "onchain",
      "Bank": "bank",
    };
    return mapping[wallet] || "virtual";
  };

  // Fetch activities when activity is selected
  useEffect(() => {
    const fetchActivities = async () => {
      if (!activity || !activity.wallet || !activity.symbol) {
        setMovements([]);
        return;
      }

      setIsLoadingActivities(true);
      try {
        const requestData = {
          limit: 50,
          page_number: 1,
          symbol: activity.symbol,
          trx_status: getTrxStatus(activity.wallet),
        };

        const response = await walletSendReceiveActivities(requestData);
        
        if (response?.data?.data) {
          // Transform API response to movements format
          const userData = JSON.parse(localStorage.getItem("user"))?.data?.user || {};
          const userAddress = userData.address || "";
          
          const transformedMovements = response.data.data.map((item) => {
            // Determine direction based on transaction type and Transfer field
            let direction = "in";
            
            // Check if Transfer field exists and matches user address
            if (item.Transfer) {
              // If Transfer matches user address, it's likely a send (out)
              // Otherwise it's a receive (in)
              direction = item.Transfer.toLowerCase() === userAddress.toLowerCase() ? "out" : "in";
            } else if (item.type) {
              // For contract or other types, try to infer from type
              if (item.type.includes("send") || item.type.includes("withdraw")) {
                direction = "out";
              } else if (item.type.includes("receive") || item.type.includes("deposit")) {
                direction = "in";
              }
            }

            return {
              wallet: activity.wallet,
              symbol: item.Symbol || activity.symbol,
              direction,
              amount: parseFloat(item.Amount) || 0,
              hash: item.hash || item.Hash || "",
              fee: item.fee || item.fee || "",
              ref: item.ref || item.Ref || "",
              ts: item.Date || new Date().toLocaleString(),
              desc: item.Name || "",
              type: item.type || "transaction",
            };
          });

          setMovements(transformedMovements);
        } else {
          setMovements([]);
        }
      } catch (error) {
        console.error("Error fetching wallet activities:", error);
        setMovements([]);
        openNotification("Error", "Failed to fetch wallet activities");
      } finally {
        setIsLoadingActivities(false);
      }
    };

    fetchActivities();
  }, [activity]);

  const handleRefreshUpcoming = async () => {
    try {
      await userInternalWalletBalanceRefresh();
      // Refresh pending balance after refresh
      const res = await getUserPendingBalanceWebapp();
      setPendingBalance(res?.data?.data || []);
    } catch (error) {
      console.error("Error refreshing upcoming balance:", error);
      toast.error("Error refreshing upcoming balance");
    }
  };

  return (
    <>
      <GlobalStyle />
      <Page>
        {(kycStatus === "" || kycStatus === "created" || kycStatus === "declined" || kycStatus === "expired") && (
          <StatusBanner level={messageLevel} text={messageText} onActionClick={handleKYC} />
        )}

        {section === "Wallet" && (
          <>
            <WalletOverview
              totalBalance={totalBalanceCalc}
              virtualTotal={virtualTotal}
              onchainTotal={onchainTotal}
              bankTotal={bankTotal}
            />

            {/* Wallet blocks */}
            <WalletBlock
              title="Virtual Wallet"
              subtitle="Custodial balance for in-app activities, marketplace, and instant payments."
              wallet="Virtual"
              tab={tabVirtual}
              onChangeTab={setTabVirtual}
              items={virtualItems}
              allItems={transformVirtualWalletDataMemo}
              categoryTotals={virtualTotalsAll}
              onWalletDetails={() => handleWalletDetails("Virtual")}
              onRefresh={() => {
                dispatch(fetchAllCurrencies1());
              }}
              pendingBalance={pendingBalance}
              onRefreshUpcoming={handleRefreshUpcoming}
              showAddressView={addressViewWallet === "Virtual"}
              addresses={virtualAddresses}
              onBack={() => handleBackFromAddress("Virtual")}
              activity={activity}
              movements={movements}
              onRowClick={handleRowClick}
              onBackFromActivity={handleBackFromActivity}
              isLoadingActivities={isLoadingActivities}
            />

            <WalletBlock
              title="On-chain Wallet"
              subtitle="On-chain balance for direct interaction with the blockchain and smart contracts."
              wallet="On-chain"
              tab={tabOnchain}
              onChangeTab={setTabOnchain}
              items={onchainItems}
              allItems={transformOnchainWalletDataMemo}
              categoryTotals={onchainTotalsAll}
              onWalletDetails={() => handleWalletDetails("On-chain")}
              onRefresh={() => {
              }}
              showAddressView={addressViewWallet === "On-chain"}
              addresses={onchainAddresses}
              onBack={() => handleBackFromAddress("On-chain")}
              activity={activity}
              movements={movements}
              onRowClick={handleRowClick}
              onBackFromActivity={handleBackFromActivity}
              isLoadingActivities={isLoadingActivities}
            />

            <BankWalletBlock
              items={bankItems}
              onWalletDetails={() => handleWalletDetails("Bank")}
              onRefresh={() => {
                // Bank wallet refresh (demo)
              }}
              pendingBalance={pendingBalance}
              onRefreshUpcoming={handleRefreshUpcoming}
              showAddressView={addressViewWallet === "Bank"}
              addresses={bankAddresses}
              onBack={() => handleBackFromAddress("Bank")}
              activity={activity}
              movements={movements}
              onRowClick={(currency) => handleRowClick("Bank", currency)}
              onBackFromActivity={handleBackFromActivity}
              isLoadingActivities={isLoadingActivities}
            />

            {/* Swap / IBAN / Send-Receive */}
            <SwapCard swap={swap} onChangeSwap={setSwap} onSwap={handleSwap} />

            <IbanCard movements={IBAN_MOVEMENTS} onAction={handleIbanAction} />

            <SendWithdrawCard lastWallet={lastWallet} />
          </>
        )}

        {section === "Home" && (
          <Card>
            <h2 style={{ marginBottom: 8 }}>Home</h2>
            <p style={{ color: "#94a3b8", fontSize: 13, marginTop: 4 }}>
              Dashboard sintetica: qui puoi inserire KPI, ultime attività e link rapidi verso Wallet, OTC, Project e Trade.
            </p>
          </Card>
        )}

        {section === "Account" && (
          <Card>
            <h2 style={{ marginBottom: 8 }}>Account</h2>
            <p style={{ color: "#94a3b8", fontSize: 13, marginTop: 4 }}>
              Impostazioni utente, sicurezza (2FA), KYC/KYB e preferenze di notifica.
            </p>
            <div style={{ marginTop: 10 }}>
              <h3 style={{ fontSize: 14, marginBottom: 6 }}>KYC Status</h3>
              <div style={{ padding: "4px 10px", borderRadius: 999, border: "1px solid #1f2937", background: "rgba(248,113,113,0.16)", fontSize: 12, display: "inline-block" }}>
                Current: <strong>{kycStatus || "none"}</strong>
              </div>
            </div>
          </Card>
        )}
      </Page>

      <NotificationModal notification={notification} onClose={() => setNotification(null)} />
    </>
  );
}
